import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useParams } from "#routers/hooks";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import {
  useIsFeatureAvailable,
  useLegacyIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useQuery } from "@tanstack/react-query";
import { Page } from "@validereinc/common-components";
import { Insights, InsightsAdapterQueryKeys } from "@validereinc/domain";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import classNames from "classnames/bind";
import React, { useEffect, useRef } from "react";
import { INSIGHTS_DETAILS_DASHBOARD_BREADCRUMB } from ".";
import { INSIGHTS_BREADCRUMB } from "../..";
import { DASHBOARDS_BREADCRUMB } from "../../..";
import styles from "./InsightsDetailsPage.module.scss";

const cx = classNames.bind(styles);

export const InsightsDetailsPage = () => {
  const [isInsightsAvailableLegacy] = useLegacyIsFeatureAvailable({
    featureFlagQuery: ["core/insights_dashboard"],
    permissionQuery: ["core:insights"],
    featureId: "dashboards",
  });
  const [isInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "dashboards:insights",
    permissionQuery: "insights_dashboards:read",
  });
  const iframeRef = useRef(null);
  const { insightsId } = useParams<{ insightsId: string }>();

  const [shouldUseV2Insights] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
    permissionQuery: PermissionsMap.InsightsDashboardsRead,
  });

  const { data: dashboardDetailsData, isLoading: isLoadingDashboards } =
    useQuery({
      queryKey: InsightsAdapterQueryKeys.getDashboardDetails({ insightsId }),
      queryFn: () => Insights.getDashboardDetails({ insightsId }),
      enabled: Boolean(!shouldUseV2Insights && insightsId),
    });

  const { data: dashboardDetailsDataV2, isLoading: isLoadingDashboardsV2 } =
    useQuery({
      queryKey: InsightsAdapterQueryKeys.getDashboardDetailsV2({ insightsId }),
      queryFn: () => Insights.getDashboardDetailsV2({ insightsId }),
      enabled: Boolean(shouldUseV2Insights && insightsId),
    });

  const dashboardDetails = shouldUseV2Insights
    ? dashboardDetailsDataV2
    : dashboardDetailsData;
  const isLoading = shouldUseV2Insights
    ? isLoadingDashboardsV2
    : isLoadingDashboards;

  useEffect(() => {
    async function embedDashboard() {
      if (!dashboardDetails?.embed_url || !iframeRef.current) {
        return;
      }

      const embeddingContext =
        await QuickSightEmbedding.createEmbeddingContext();

      const frameOptions = {
        url: dashboardDetails.embed_url,
        container: iframeRef.current,
        withIframePlaceholder: true,
        resizeHeightOnSizeChangedEvent: true,
      };

      const contentOptions = {
        toolbarOptions: {
          export: true,
          undoRedo: false,
          reset: false,
        },
      };

      await embeddingContext.embedDashboard(frameOptions, contentOptions);
    }

    embedDashboard();
  }, [dashboardDetails?.embed_url]);

  if (!isInsightsAvailable && !isInsightsAvailableLegacy) {
    return <AccessDeniedLayout />;
  }

  return (
    <Page
      isLoading={isLoading}
      title={dashboardDetails?.dashboard?.name}
      breadcrumbs={useBreadcrumbs(
        [
          DASHBOARDS_BREADCRUMB,
          INSIGHTS_BREADCRUMB,
          INSIGHTS_DETAILS_DASHBOARD_BREADCRUMB,
        ],
        { 2: dashboardDetails?.dashboard.name }
      )}
      category="Insights"
    >
      <div className={cx("iframeContainer")}>
        <div ref={iframeRef} />
      </div>
    </Page>
  );
};
